import type { FlowInput } from '@shared/types/flows';

export interface LaunchInput {
  key: string;
  label: string;
  value: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readSnapshotInputs = (flowSnapshot: unknown): FlowInput[] => {
  if (!isRecord(flowSnapshot) || !Array.isArray(flowSnapshot.inputs)) return [];
  return flowSnapshot.inputs.filter(
    (input): input is FlowInput => isRecord(input) && typeof input.key === 'string'
  );
};

// Keys the flow does not declare still read as words, never as snake_case.
const humanizeKey = (key: string): string => {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

const formatValue = (value: unknown, input: FlowInput | undefined): string => {
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  if (typeof value === 'number') return Number.isFinite(value) ? value.toLocaleString('fr-FR') : '';
  if (Array.isArray(value)) {
    return value.map((item) => formatValue(item, undefined)).filter(Boolean).join(', ');
  }
  if (typeof value !== 'string') return '';
  const text = value.trim();
  const option = input?.options?.find((candidate) => candidate.value === text);
  return option?.label ?? text;
};

/**
 * The values a run was launched with, labelled by the flow's own inputs as they
 * were at launch (`flow_snapshot.inputs`). Internal keys (`__*`) and empty
 * values are left out.
 */
export const extractLaunchInputs = (
  inputValues: unknown,
  flowSnapshot: unknown
): LaunchInput[] => {
  if (!isRecord(inputValues)) return [];
  const declared = readSnapshotInputs(flowSnapshot);
  const declaredKeys = new Set(declared.map((input) => input.key));
  const orderedKeys = [
    ...declared.map((input) => input.key).filter((key) => key in inputValues),
    ...Object.keys(inputValues).filter((key) => !declaredKeys.has(key)),
  ];

  return orderedKeys.flatMap((key): LaunchInput[] => {
    if (key.startsWith('__')) return [];
    const input = declared.find((candidate) => candidate.key === key);
    const value = formatValue(inputValues[key], input);
    if (!value) return [];
    return [{ key, label: input?.label?.trim() || humanizeKey(key), value }];
  });
};
